/**
 * Views placed in a page, drawn live in the browser.
 *
 * The editor paints embeds in its own React root, so each view brings its own
 * `TrackersUIProvider` over the project's tracker room, as the desktop's frame
 * does. The renderer is installed once and reads the current room through a
 * small store, so a view painted before the room opened fills in when it does,
 * and one painted in another project never reads this project's items: the
 * reach is this team project only (`placedViewInReach`), so a view of another
 * project, or of someone's own items, shows its link instead. A view of
 * someone's Personal pages (a `local` link) is kept on their device: it says
 * so before the room is even open, and opens the console's open-in-app page.
 */
import { lazy, Suspense, useEffect, useSyncExternalStore, type ComponentProps, type JSX } from 'react';
import {
  loadPlacedViewEmbed,
  setBrowserPlacedViewRenderer,
  type BrowserPlacedViewRenderer,
  type PlacedViewEmbedProps,
} from '@nimbalyst/collab-bundle/editor';
import { TrackersUIProvider } from '@nimbalyst/collab-bundle/trackers-ui';
import { embeddedReadOnlyTrackerEmbeds, isEmbedded } from '../embedded/embedMarker';

type EmbedProps = PlacedViewEmbedProps;

export interface PlacedViewRoom {
  dataSource: ComponentProps<typeof TrackersUIProvider>['dataSource'];
  identity: ComponentProps<typeof TrackersUIProvider>['identity'];
  teamMembers?: ComponentProps<typeof TrackersUIProvider>['teamMembers'];
  orgId: string;
  /** The team project id, as console links name it. */
  projectId: string;
  onOpenFullView?: EmbedProps['onOpenFullView'];
  onOpenItem: (itemId: string) => void;
  onOpenPage: (uri: string) => void;
  onOpenLink: (href: string) => void;
}

let current: PlacedViewRoom | null = null;
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const read = () => current;

function publish(room: PlacedViewRoom | null): void {
  current = room;
  for (const listener of listeners) listener();
}

const PlacedViewEmbed = lazy(() => loadPlacedViewEmbed().then((module) => ({ default: module.PlacedViewEmbed })));

function AuthorDeviceViewNote({ src, label }: { src: string; label: string }): JSX.Element {
  const room = useSyncExternalStore(subscribe, read, read);
  return (
    <div
      className="placed-view-author-device my-3 rounded-lg border border-dashed border-nim bg-nim-secondary px-3 py-2 text-xs text-nim-muted"
      contentEditable={false}
      data-testid="placed-view-author-device"
    >
      {label || 'View'}: a view of its author&apos;s Personal pages, on the author&apos;s device.
      <a
        className="ml-1 text-nim-link hover:underline"
        href={src}
        onClick={(event) => {
          if (!room) return;
          event.preventDefault();
          room.onOpenLink(src);
        }}
      >
        Open
      </a>
    </div>
  );
}

function HostedPlacedView({ target, label, attrs, onAttrsChange }: Pick<EmbedProps, 'target' | 'label' | 'attrs' | 'onAttrsChange'>): JSX.Element | null {
  const room = useSyncExternalStore(subscribe, read, read);
  if (!room?.dataSource) {
    return <div className="placed-view-pending text-xs text-nim-faint" contentEditable={false}>{label || 'View'}: opening the project's items…</div>;
  }
  return (
    // Placed views are read-only in the phone app in v1: no cell edits, and
    // settings changes stay local instead of rewriting the page.
    <div className="placed-view-embed-frame" contentEditable={false} {...embeddedReadOnlyTrackerEmbeds()}>
      <TrackersUIProvider dataSource={room.dataSource} identity={room.identity} teamMembers={room.teamMembers}>
        <Suspense fallback={null}>
          <PlacedViewEmbed
            target={target}
            label={label}
            attrs={attrs}
            onAttrsChange={isEmbedded() ? undefined : onAttrsChange}
            reach={{ team: { orgId: room.orgId, projectId: room.projectId }, local: false }}
            onOpenFullView={room.onOpenFullView}
            onOpenItem={room.onOpenItem}
            onOpenPage={room.onOpenPage}
            onOpenLink={room.onOpenLink}
          />
        </Suspense>
      </TrackersUIProvider>

    </div>
  );
}

const renderPlacedView: BrowserPlacedViewRenderer = ({ src, target, label, attrs, onAttrsChange }) => (
  target.scope === 'local'
    ? <AuthorDeviceViewNote src={src} label={label} />
    : <HostedPlacedView target={target} label={label} attrs={attrs} onAttrsChange={onAttrsChange} />
);

/** Publishes this project's room to every placed view while Pages is open. */
export function usePlacedViewHost(room: PlacedViewRoom | null): void {
  useEffect(() => setBrowserPlacedViewRenderer(renderPlacedView), []);
  useEffect(() => {
    publish(room);
    return () => {
      if (current === room) publish(null);
    };
  }, [room]);
}
